import React, { useContext } from 'react';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { LineType, PurchaseApplicationLineResponse, PurchaseLineResponse } from '@/generated/data.schemas';
import { useGetTranslations } from '@/generated/translations';
import { BasketLine } from '../BasketLine';
import { ApplicationHeadLine } from '../BasketLine/ApplicationHeadLine';
import styles from './ApplicationLine.module.scss';

export interface ApplicationLineProps {
	applicationLine: PurchaseApplicationLineResponse;
	isQuickBasket?: boolean;
	enableDiscount?: boolean;
	toggleDiscountFeature?: () => void;
}

export const ApplicationLine: React.FunctionComponent<ApplicationLineProps> = ({
	applicationLine,
	isQuickBasket,
	enableDiscount,
	toggleDiscountFeature,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const {
		id,
		image,
		additionalBasketLines,
		listPrice,
		name,
		price,
		productMasterId,
		productVariantId,
		quantity,
		url,
		productNumber,
		customerDiscount,
		salesRepDiscount,
		categories,
	} = applicationLine;

	const applicationBasketLine: PurchaseLineResponse = {
		id,
		productMasterId,
		productVariantId,
		image,
		name,
		url,
		quantity,
		price,
		listPrice,
		productNumber,
		lineType: LineType.Application,
		customerDiscount,
		salesRepDiscount,
	};

	// The companion ( secondary unit ) and its own sub-lines are flattened into additionalBasketLines.
	// The companion is identified by its line type; its sub-lines point back to it via parentBasketLineId.
	const companionLine = additionalBasketLines.find((line) => line.lineType === LineType.CompanionProduct);
	const companionChildLines = companionLine
		? additionalBasketLines.filter((line) => line.parentBasketLineId === companionLine.id)
		: [];

	const primaryAdditionalLines = additionalBasketLines.filter(
		(line) =>
			line.lineType !== LineType.CompanionProduct &&
			(!companionLine || line.parentBasketLineId !== companionLine.id),
	);

	const renderTypedLines = (lines: PurchaseLineResponse[]) => (
		<>
			{lines
				.filter((line) => line.lineType === LineType.Insert)
				.map((product) => (
					<BasketLine
						variant={'additional'}
						key={product.id}
						line={product}
						isFull={!isQuickBasket}
						isQuantityReadOnly
						isQuickBasket={isQuickBasket}
						enableDiscount={enableDiscount}
					/>
				))}
			{lines
				.filter((line) => line.lineType === LineType.AddOn)
				.map((product) => (
					<BasketLine
						key={product.id}
						variant={'additional'}
						line={product}
						isFull={!isQuickBasket}
						isQuantityReadOnly={isQuickBasket}
						isQuickBasket={isQuickBasket}
						enableDiscount={enableDiscount}
					/>
				))}
			{lines
				.filter((line) => line.lineType === LineType.MandatoryAddOn)
				.map((product) => (
					<BasketLine
						key={product.id}
						variant={'additional'}
						line={product}
						isFull={!isQuickBasket}
						isQuantityReadOnly
						isRemoveDisabled
						isQuickBasket={isQuickBasket}
						enableDiscount={enableDiscount}
					/>
				))}
			{lines
				.filter((line) => line.lineType === LineType.Accessory)
				.map((product) => (
					<BasketLine
						key={product.id}
						variant={'additional'}
						line={product}
						isFull={!isQuickBasket}
						isQuantityReadOnly={isQuickBasket}
						isQuickBasket={isQuickBasket}
						enableDiscount={enableDiscount}
					/>
				))}
		</>
	);

	return (
		<li>
			{!isQuickBasket && (
				<ApplicationHeadLine
					name={name}
					categories={categories}
					enableDiscount={enableDiscount}
					toggleDiscountFeature={toggleDiscountFeature}
				/>
			)}
			<BasketLine
				line={{ ...applicationBasketLine, name }}
				variant={'applicationLine'}
				label={translations?.data.appBuilderSolution.primary}
				isFull={!isQuickBasket}
				isQuantityReadOnly={isQuickBasket}
				categories={categories}
				isQuickBasket={isQuickBasket}
				enableDiscount={enableDiscount}
			/>

			{renderTypedLines(primaryAdditionalLines)}

			{companionLine && (
				<div className={styles.companion}>
					<BasketLine
						variant={'additional'}
						label={translations?.data.appBuilderSolution.secondary}
						line={companionLine}
						isFull={!isQuickBasket}
						isQuantityReadOnly={isQuickBasket}
						isQuickBasket={isQuickBasket}
						enableDiscount={enableDiscount}
					/>
					{renderTypedLines(companionChildLines)}
				</div>
			)}
		</li>
	);
};
